import React, { useState, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchRecipes } from '../store/recipesSlice';
import { fetchPricesForFoods, formatPrice } from '../store/pricesSlice';

const ShoppingList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items: recipes, status, error } = useAppSelector((state) => state.recipes);
  const pricesByFoodId = useAppSelector((state) => state.prices.byFoodId);
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [haveSet, setHaveSet] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (status === 'idle') dispatch(fetchRecipes());
  }, [dispatch, status]);

  const aggregatedIngredients = useMemo(() => {
    const map = new Map<string, { displayName: string; measurements: string[]; foodId: number | undefined }>();
    recipes
      .filter(r => selectedIds.has(r.id))
      .forEach(recipe => {
        recipe.ingredients.forEach(ing => {
          if (!ing.food_name?.trim()) return;
          const key = ing.food_name.toLowerCase().trim();
          if (!map.has(key)) {
            map.set(key, { displayName: ing.food_name, measurements: [], foodId: ing.food_id ?? undefined });
          }
          const m = ing.measurement.trim();
          if (m) map.get(key)!.measurements.push(m);
        });
      });
    return Array.from(map.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [recipes, selectedIds]);

  const toBuy = useMemo(
    () => aggregatedIngredients.filter(i => !haveSet.has(i.displayName.toLowerCase())),
    [aggregatedIngredients, haveSet]
  );

  const toggleRecipe = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleHave = (key: string) => {
    setHaveSet(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const goToStep2 = () => {
    setHaveSet(new Set());
    setStep(2);
    const foodIds = aggregatedIngredients
      .map((i) => i.foodId)
      .filter((id): id is number => id != null);
    if (foodIds.length > 0) dispatch(fetchPricesForFoods(foodIds));
  };

  const selectAll = () => setSelectedIds(new Set(recipes.map(r => r.id)));
  const clearAll = () => setSelectedIds(new Set());

  if (status === 'loading') return <div className="text-army-600 py-12 text-center">Loading recipes...</div>;
  if (status === 'failed') return <div className="text-rose-400 py-12 text-center">Error: {error}</div>;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <h2 className="text-2xl font-bold text-white">Shopping List</h2>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className={step === 1 ? 'text-neon font-semibold' : ''}>1. Pick recipes</span>
          <span>→</span>
          <span className={step === 2 ? 'text-neon font-semibold' : ''}>2. What do you have?</span>
        </div>
      </div>

      {step === 1 && (
        <div className="flex gap-6 items-start">
          {/* Recipe selector */}
          <div className="w-64 shrink-0 bg-navy-900 border border-navy-800 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-navy-800">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Recipes</span>
              <div className="flex gap-3">
                <button onClick={selectAll} className="text-xs text-neon hover:brightness-90 transition-all">All</button>
                <button onClick={clearAll} className="text-xs text-gray-500 hover:text-white transition-colors">Clear</button>
              </div>
            </div>
            {recipes.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-500 text-center">No recipes yet.</p>
            ) : (
              <ul className="divide-y divide-navy-800 max-h-[70vh] overflow-y-auto">
                {recipes.map(recipe => (
                  <li key={recipe.id}>
                    <label className="flex items-center gap-3 px-4 py-3 hover:bg-navy-800 transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(recipe.id)}
                        onChange={() => toggleRecipe(recipe.id)}
                        className="accent-neon w-4 h-4 shrink-0"
                      />
                      <span className="text-sm text-gray-300">{recipe.name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Preview + continue */}
          <div className="flex-1 min-w-0 flex flex-col gap-4">
            <div className="bg-navy-900 border border-navy-800 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-navy-800">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {aggregatedIngredients.length} ingredient{aggregatedIngredients.length !== 1 ? 's' : ''}
                </span>
              </div>
              {selectedIds.size === 0 ? (
                <p className="py-16 text-center text-gray-500 text-sm">Select recipes to build your list.</p>
              ) : aggregatedIngredients.length === 0 ? (
                <p className="py-16 text-center text-gray-500 text-sm">Selected recipes have no ingredients.</p>
              ) : (
                <ul className="divide-y divide-army-900">
                  {aggregatedIngredients.map(({ displayName, measurements }) => (
                    <li key={displayName} className="flex items-baseline gap-3 px-5 py-3">
                      <span className="font-medium text-white">{displayName}</span>
                      {measurements.length > 0 && (
                        <>
                          <span className="text-army-700">—</span>
                          <span className="text-gray-400 text-sm">{measurements.join(', ')}</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {aggregatedIngredients.length > 0 && (
              <div className="flex justify-end">
                <button
                  onClick={goToStep2}
                  className="px-5 py-2.5 bg-neon hover:brightness-90 text-army-950 font-bold rounded-lg transition-all text-sm"
                >
                  Next: What do you have? →
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="flex gap-6 items-start">
          {/* Have / don't have checklist */}
          <div className="flex-1 min-w-0 bg-navy-900 border border-navy-800 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-navy-800">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Check what you already have</span>
              <button
                onClick={() => setStep(1)}
                className="text-xs text-gray-500 hover:text-white transition-colors"
              >
                ← Back
              </button>
            </div>
            <ul className="divide-y divide-army-900">
              {aggregatedIngredients.map(({ displayName, measurements }) => {
                const key = displayName.toLowerCase();
                const have = haveSet.has(key);
                return (
                  <li key={displayName}>
                    <label className={`flex items-baseline gap-3 px-5 py-3 cursor-pointer transition-colors ${have ? 'hover:bg-army-900' : 'hover:bg-army-900'}`}>
                      <input
                        type="checkbox"
                        checked={have}
                        onChange={() => toggleHave(key)}
                        className="accent-neon w-4 h-4 shrink-0 mt-0.5"
                      />
                      <span className={`font-medium transition-colors ${have ? 'text-gray-600 line-through' : 'text-white'}`}>
                        {displayName}
                      </span>
                      {measurements.length > 0 && (
                        <>
                          <span className="text-army-700">—</span>
                          <span className={`text-sm transition-colors ${have ? 'text-gray-700 line-through' : 'text-gray-400'}`}>
                            {measurements.join(', ')}
                          </span>
                        </>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Need to buy */}
          <div className="w-72 shrink-0 bg-navy-900 border border-navy-800 rounded-xl overflow-hidden sticky top-24">
            <div className="px-5 py-3 border-b border-navy-800">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Need to buy — {toBuy.length}
              </span>
            </div>
            {toBuy.length === 0 ? (
              <p className="py-10 text-center text-gray-500 text-sm">You have everything!</p>
            ) : (
              <>
                <ul className="divide-y divide-army-900">
                  {toBuy.map(({ displayName, measurements, foodId }) => {
                    const price = foodId != null ? pricesByFoodId[foodId] : null;
                    const effectiveCents = price
                      ? (price.on_sale && price.sale_price_cents != null ? price.sale_price_cents : price.price_cents)
                      : null;
                    return (
                      <li key={displayName} className="px-5 py-3">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-medium text-white text-sm">{displayName}</span>
                          {effectiveCents != null && (
                            <span className={`text-sm shrink-0 ${price?.on_sale ? 'text-neon' : 'text-gray-400'}`}>
                              {formatPrice(effectiveCents)}
                            </span>
                          )}
                        </div>
                        {measurements.length > 0 && (
                          <span className="text-gray-500 text-xs">{measurements.join(', ')}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {(() => {
                  const totalCents = toBuy.reduce((sum, { foodId }) => {
                    if (foodId == null) return sum;
                    const price = pricesByFoodId[foodId];
                    if (!price) return sum;
                    return sum + (price.on_sale && price.sale_price_cents != null ? price.sale_price_cents : price.price_cents);
                  }, 0);
                  const priceCount = toBuy.filter(({ foodId }) => foodId != null && pricesByFoodId[foodId] != null).length;
                  if (priceCount === 0) return null;
                  return (
                    <div className="px-5 py-3 border-t border-navy-800 flex justify-between items-baseline">
                      <span className="text-xs text-gray-500">
                        Estimated total{priceCount < toBuy.length ? ` (${priceCount}/${toBuy.length} items)` : ''}
                      </span>
                      <span className="text-sm font-semibold text-white">{formatPrice(totalCents)}</span>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShoppingList;
